import { useState, useEffect, useRef } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import logo from "../../assets/images/logo.svg";
import { httprequest } from "../../data/api";
import "./ReferralDashboard.css";

import ReferralOverview from "./components/ReferralOverview";
import ReferralBank from "./components/ReferralBank";
import ReferralSettings from "./components/ReferralSettings";
import ReferralProfile from "./components/ReferralProfile";
import ReferralWithdrawHistory from "./components/ReferralWithdrawHistory";
import ReferralReferredUsers from "./components/ReferralReferredUsers";

const ReferralDashboard = () => {
  const [activePill, setActivePill] = useState("Dashboard");
  const [dashboardData, setDashboardData] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [referrers, setReferrers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isReferrersLoading, setIsReferrersLoading] = useState(true);
  const [currentView, setCurrentView] = useState("dashboard");
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const response = await httprequest.get("/api/v1/referrals/referrer/overview");
        setDashboardData(response?.data?.data || response?.data);
      } catch (error) {
        toast.error("Failed to load dashboard data");
      } finally {
        setIsLoading(false);
      }
    };

    const fetchReferrers = async () => {
      try {
        const response = await httprequest.get("/api/v1/referrals/referrer/schools");
        const dataList = response?.data?.data?.data || response?.data?.data || response?.data || [];
        setReferrers(Array.isArray(dataList) ? dataList : []);
      } catch (error) {
        toast.error("Failed to load referrers list");
      } finally {
        setIsReferrersLoading(false);
      }
    };

    const fetchProfileData = async () => {
      try {
        const response = await httprequest.get("/api/v1/referrals/referrer/profile");
        setProfileData(response?.data?.data || response?.data);
      } catch (error) {
        // silently fail
      }
    };

    fetchDashboardData();
    fetchReferrers();
    fetchProfileData();
  }, []);

  return (
    <div className="ref-dash-container">
      {/* Navbar */}
      <nav className="ref-dash-navbar">
        <div className="ref-dash-logo">
          <img src={logo} alt="Shelf21 Logo" />
        </div>

        <div className="ref-dash-nav-pills">
          <div
            className={`ref-dash-pill ${activePill === "Dashboard" ? "active" : ""}`}
            onClick={() => {
              setActivePill("Dashboard"); setCurrentView("dashboard");
            }}
          >
            <Icon icon="fluent:grid-20-regular" /> Dashboard
          </div>
          <div
            className={`ref-dash-pill ${activePill === "Withdraw" ? "active" : ""}`}
            onClick={() => {
              setActivePill("Withdraw"); setCurrentView("withdraw_history");
            }}
          >
            <Icon icon="fluent:wallet-20-regular" /> Withdraw
          </div>
          <div
            className={`ref-dash-pill ${activePill === "Referred User" ? "active" : ""}`}
            onClick={() => {
              setActivePill("Referred User"); setCurrentView("referred");
            }}
          >
            <Icon icon="fluent:people-20-regular" /> Referred User
          </div>
        </div>

        <div className="ref-dash-user-section">
          <div className="ref-dash-balance">
            <Icon icon="ph:wallet" className="ref-dash-wallet-icon" />
            <div className="ref-dash-balance-info">
              <span className="ref-dash-balance-label">Balance</span>
              <span className="ref-dash-balance-amount">
                #{isLoading ? "..." : (dashboardData?.available_points || 0).toLocaleString()}
              </span>
            </div>
          </div>
          <div className="ref-dash-user-profile" ref={dropdownRef} onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}>
            <img src={`https://ui-avatars.com/api/?name=${profileData?.first_name || "User"}+${profileData?.last_name || ""}&background=random`} alt="User Avatar" className="ref-dash-avatar" />
            <Icon icon="bx:chevron-down" className="ref-dash-dropdown-icon" />

            {isProfileDropdownOpen && (
              <div className="ref-profile-dropdown">
                <div className="ref-dropdown-item" onClick={(e) => {
                  e.stopPropagation(); setCurrentView("profile"); setIsProfileDropdownOpen(false);
                }}
                >
                  <Icon icon="bx:user" /> Profile
                </div>
                <div className="ref-dropdown-item" onClick={(e) => {
                  e.stopPropagation(); setCurrentView("settings"); setIsProfileDropdownOpen(false);
                }}
                >
                  <Icon icon="bx:cog" /> Settings
                </div>
                <div style={{ borderTop: "1px solid #EAEAEA", margin: "4px 0" }} />
                <div className="ref-dropdown-item" onClick={(e) => {
                  e.stopPropagation();
                  localStorage.removeItem("token");
                  localStorage.removeItem("userToken");
                  window.location.href = "/auth/referrer";
                }}
                style={{ color: "#050404ff" }}
                >
                  <Icon icon="majesticons:logout-line" /> Log Out
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Conditional Rendering of Sub-components */}
      {currentView === "dashboard" && (
        <ReferralOverview
          dashboardData={dashboardData}
          profileData={profileData}
          isLoading={isLoading}
          referrers={referrers}
          isReferrersLoading={isReferrersLoading}
          setCurrentView={setCurrentView}
          setActivePill={setActivePill}
        />
      )}

      {currentView === "referred" && (
        <ReferralReferredUsers
          referrers={referrers}
          isReferrersLoading={isReferrersLoading}
          setCurrentView={setCurrentView}
          setActivePill={setActivePill}
        />
      )}

      {currentView === "bank" && (
        <ReferralBank
          dashboardData={dashboardData}
          setDashboardData={setDashboardData}
          setCurrentView={setCurrentView}
          isLoading={isLoading}
        />
      )}

      {currentView === "withdraw_history" && (
        <ReferralWithdrawHistory
          setCurrentView={setCurrentView}
        />
      )}

      {currentView === "settings" && (
        <ReferralSettings
          setCurrentView={setCurrentView}
        />
      )}

      {currentView === "profile" && (
        <ReferralProfile
          profileData={profileData}
          dashboardData={dashboardData}
          setCurrentView={setCurrentView}
          setProfileData={setProfileData}
        />
      )}
    </div>
  );
};

export default ReferralDashboard;
